// InsuranceCard Component
// This file defines a card component for displaying insurance details, with support for mini mode, badges, and image lightbox.

import { useState } from "react";
import { Insurance } from "@/types/procedure";
import { Button, Card, Lightbox } from "@/vendor/Forge/dist";
import {
  InsuranceText,
  InsuranceWrapper,
  BottomBorderWrapper,
  Row,
  LabeledComponent,
  WarningTemplate,
  InfoTemplate,
} from "./components.stitches";

import Accordion from "../Accordion";
import { ZoomInIcon } from "@/components/Icons";

/**
 * InsuranceCard Component
 * Displays insurance details in a card format, with optional mini mode, badge, and image lightbox.
 *
 * @param insurance - The insurance object to display
 * @param config - Optional configuration for icons, click handlers, badges, mini mode, and alerts
 */
const InsuranceCard = ({
  insurance,
  config = {
    mini: false,
    iconDisabled: false,
    disableAlert: false,
  },
}: {
  insurance: Insurance;
  config?: {
    icon?: React.ReactNode;
    onIconClick?: (insurance: Insurance) => void;
    iconDisabled?: boolean;
    badge?: React.ReactNode;
    mini?: boolean;
    disableAlert?: boolean;
  };
}) => {
  // State for showing the enlarged insurance image
  const [showInsuranceEnlarged, setShowInsuranceEnlarged] = useState(false);
  const NOT_AVAILABLE = "Not Available";

  // Maps sequence numbers to display text
  const sequenceMapper: {
    [key: string]: string;
  } = {
    1: "Primary",
    2: "Secondary",
    3: "Tertiary",
  };

  // Show info template if self-pay and alerts are not disabled
  if (insurance.insurancePlan === "*SELF PAY*" && !config.disableAlert) {
    return <InfoTemplate text="Patient is self-pay" />;
  }

  return (
    <Card
      style={{
        padding: "20px",
        width: "500px",
        borderRadius: "20px",
        height: "100%",
      }}
      padded={false}
      key={insurance.insuranceNumber}
    >
      <Row css={{ gap: "10px", flexWrap: "nowrap" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          <InsuranceWrapper>
            {/* Header row with plan display name, number, and optional icon */}
            <div
              style={{
                display: "flex",
                gap: "24px",
              }}
            >
              <BottomBorderWrapper css={{ flex: 1 }}>
                <Row
                  css={{
                    wrap: "nowrap",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: "0",
                  }}
                >
                  <InsuranceText header css={{ maxWidth: "calc(100% - 80px)" }}>
                    {insurance.insurancePlanDisplayName} *
                    {insurance.insuranceNumber.substring(
                      insurance.insuranceNumber.length - 4,
                    )}
                  </InsuranceText>
                  {config?.icon && (
                    <Button
                      onClick={() =>
                        config.onIconClick
                          ? config.onIconClick?.(insurance)
                          : null
                      }
                      variant="tertiary"
                      style={{
                        height: "14px",
                        filter: config.iconDisabled
                          ? "grayscale(1) brightness(200%)"
                          : "none",
                      }}
                      disabled={config.iconDisabled}
                    >
                      {config?.icon}
                    </Button>
                  )}
                </Row>
                {/* Optional badge */}
                {config?.badge && config.badge}
              </BottomBorderWrapper>
            </div>

            {/* Main insurance details section */}
            <BottomBorderWrapper
              css={{
                paddingTop: "12px",
                display: "flex",
                wrap: "nowrap",
                height: "100%",
                gap: "10px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  width: "50%",
                }}
              >
                <LabeledComponent
                  component={insurance.insuranceNumber ?? NOT_AVAILABLE}
                  label="ID NUMBER:"
                  horizontal
                />
                <LabeledComponent
                  component={insurance.groupNumber ?? NOT_AVAILABLE}
                  label="GROUP NUMBER:"
                  horizontal
                />
                {/* Show plan and type unless in mini mode */}
                {!config.mini && (
                  <LabeledComponent
                    component={insurance.insurancePlan ?? NOT_AVAILABLE}
                    label="PLAN:"
                    horizontal
                  />
                )}
                {!config.mini && (
                  <LabeledComponent
                    component={insurance.insuranceType ?? NOT_AVAILABLE}
                    label="TYPE:"
                    horizontal
                  />
                )}
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  width: "50%",
                }}
              >
                <LabeledComponent
                  component={insurance.issueDate ?? NOT_AVAILABLE}
                  label="ISSUE DATE:"
                  horizontal
                />
                <LabeledComponent
                  component={insurance.expirationDate ?? NOT_AVAILABLE}
                  label="EXPIRATION DATE:"
                  horizontal
                />
                {/* Show address unless in mini mode */}
                {!config.mini && (
                  <LabeledComponent
                    component={insurance.policyAddress ?? NOT_AVAILABLE}
                    label="ADDRESS:"
                    horizontal
                  />
                )}
              </div>
            </BottomBorderWrapper>
            {/* Footer row with sequence and last updated info */}
            <Row
              css={{
                wrap: "nowrap",
                justifyContent: !config.mini ? "space-between" : "flex-end",
                alignItems: "center",
                gap: "0",
              }}
            >
              {/* Show sequence unless in mini mode */}
              {!config.mini && (
                <InsuranceText>
                  {insurance.sequencenumber
                    ? sequenceMapper[insurance.sequencenumber]
                    : NOT_AVAILABLE}
                </InsuranceText>
              )}
              <InsuranceText italic>
                Last Updated: {insurance.lastUpdated ?? NOT_AVAILABLE}
              </InsuranceText>
            </Row>
            {/* Accordion for insurance card image, only if not mini */}
            {!config.mini && (
              <Row
                css={{
                  alignItems: "center",
                  gap: "0",
                }}
              >
                <Accordion
                  data-testid="insurance-card-accordion"
                  justifyHeader="end"
                  labelText={{ open: "View Card", close: "Hide Card" }}
                >
                  {insurance.insuranceImage ? (
                    <div
                      style={{
                        width: "432px",
                        height: "600px",
                        position: "relative",
                      }}
                    >
                      <img
                        src={
                          "data:image/png;base64," + insurance.insuranceImage
                        }
                        alt="Insurance Card"
                        style={{
                          height: "100%",
                          width: "100%",
                          display: "block",
                          objectFit: "contain",
                        }}
                      />
                      <Button
                        variant="tertiary"
                        data-testid="zoom-in-button"
                        onClick={() => setShowInsuranceEnlarged(true)}
                        style={{
                          position: "absolute",
                          top: "50%",
                          right: "50%",
                          transform: "translate(50%, -50%)",
                        }}
                      >
                        <ZoomInIcon />
                      </Button>
                    </div>
                  ) : (
                    <WarningTemplate text="No insurance image found." />
                  )}
                </Accordion>
              </Row>
            )}
          </InsuranceWrapper>
        </div>
      </Row>
      {/* Lightbox for enlarged insurance card image */}
      <Lightbox
        height="full"
        width="full"
        show={showInsuranceEnlarged}
        includeButtonRow={false}
        headerText={`$ {
          insurance.insurancePlanDisplayName as string
        } * ${insurance.insuranceNumber.substring(
          insurance.insuranceNumber.length - 4,
        )}`}
        onHide={() => setShowInsuranceEnlarged(false)}
      >
        <div style={{ height: "100%", width: "100%" }}>
          <img
            src={"data:image/png;base64," + insurance.insuranceImage}
            alt="Insurance Card"
            data-testid="insurance-card-image"
            style={{
              width: "100%",
              display: "block",
              objectFit: "contain",
            }}
          />
        </div>
      </Lightbox>
    </Card>
  );
};

export default InsuranceCard;
